import { FeatureModal } from 'views/components';
import { SVGContainer } from './styled';
import { configActions } from 'core/Config';
import flatten from 'lodash/flatten';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    ButtonFooter,
    Checkbox,
    Container,
    ExpandButton,
    FormGroup,
    Input,
    ZoomableContainer
} from 'mol-ui';
import {
    ChatbotNode,
    NodeModal
} from './components';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
    useArray,
    useCheckbox,
    useDispatch,
    useInput,
    useObjectState,
    useSelector,
    useThrottle,
    useToggle
} from 'hooks';

const ChatbotFeatureConfiguration = () => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { primaryColor, primaryLighterColor } = useTheme();

    const {
        featureConfiguration = [],
        featureConfigurationLoading,
        featureConfigurationProcessing
    } = useSelector('config');
    const [autosaveInput, autosave] = useCheckbox(true);
    const [currentConfiguration, setCurrentConfiguration, { replaceValues }] = useArray([], { type: 'object' });
    const [currentZoom, setCurrentZoom] = useState(1);
    const [defaultXInput, defaultX] = useInput('0');
    const [defaultYInput, defaultY] = useInput('0');
    const [dragging, setDragging] = useState(null);
    const [editing, setEditing] = useState(null);
    const [featureInput, feature, setFeature] = useInput('');
    const [finishingPointCheck, finishingPoint] = useCheckbox(true);
    const [nodeHeights, , , setNodeHeightsValue] = useObjectState({});
    const [renderLines, setRenderLines] = useState(true);
    const [searchChildrenInput, searchChildren] = useInput('');
    const [searchNodeInput, searchNode] = useInput('');
    const [searchParentInput, searchParent] = useInput('');
    const [featureModal, toggleFeatureModal] = useToggle(false);

    const highlightedNodes = useMemo(() => {
        const getConnectedNodes = () => flatten(featureConfiguration.map(({ nodes }) => (nodes ?? []).map(({ id }) => id)));

        const stringToArray = str => {
            if (!str) return [];
            return str.split(',').map(node => parseInt(node.trim(), 10));
        };

        const highlightedNodes = stringToArray(searchNode);

        const searchParentNodes = stringToArray(searchParent);
        const highlightedParents = featureConfiguration.filter(({ nodes }) => !isEmpty(intersection(nodes.map(({ id }) => id), searchParentNodes))).map(({ id }) => id);
        
        const searchChildrenNodes = stringToArray(searchChildren);
        const highlightedChildren = flatten(featureConfiguration.filter(({ id }) => searchChildrenNodes.includes(id)).map(({ nodes }) => (nodes ?? []).map(({ id }) => id)));
        
        const highlightedFinishing = !finishingPoint ? [] : [
            ...featureConfiguration.filter(({ nodes }) => isEmpty(nodes)).map(({ id }) => id),
            ...featureConfiguration.filter(({ id }) => !getConnectedNodes().includes(id)).map(({ id }) => id)
        ];

        return [...highlightedNodes, ...highlightedParents, ...highlightedChildren, ...highlightedFinishing];
    }, [featureConfiguration, finishingPoint, searchChildren, searchParent, searchNode]);

    useEffect(() => {
        if (!featureConfigurationProcessing) {
            setEditing(null);
        }
    }, [featureConfigurationProcessing, setEditing]);

    useEffect(() => {
        setCurrentConfiguration(featureConfiguration);
    }, [featureConfiguration]);

    useEffect(() => {
        if (renderLines || isEmpty(currentConfiguration)) return;
        const timeout = setTimeout(() => setRenderLines(true), 200);
        return () => {
            clearTimeout(timeout);
        };
    }, [currentConfiguration, renderLines, setRenderLines]);

    const handleDoubleClick = position => {
        if (editing) return;
        setEditing({
            feature,
            position_x: position.x,
            position_y: position.y
        });
    };

    const handleMouseUp = nodeId => {
        if (!autosave) return;
        const currentNode = currentConfiguration.find(({ id }) => id === nodeId);
        const data = {
            id: nodeId,
            position_x: currentNode?.position_x,
            position_y: currentNode?.position_y
        };
        dispatch(configActions.upsertChatbotNode(data));
    };

    const handlePositionChange = useThrottle((id, position_x, position_y) => {
        replaceValues({
            position_x,
            position_y
        }, 'id', id);
    }, 25);
    
    const handleSaveSchema = () => {
        const newSchema = currentConfiguration.map(({ id, position_x, position_y }) => ({
            id,
            position_x: parseInt(position_x ?? 0, 10),
            position_y: parseInt(position_y ?? 0, 10)
        }));
        dispatch(configActions.updateFeatureSchema({
            nodes: newSchema
        }));
    };

    const handleSearch = forceFeature => {
        if ((!feature || featureConfigurationLoading) && !forceFeature) return;
        setCurrentConfiguration([]);
        setRenderLines(false);
        dispatch(configActions.getFeatureConfiguration(forceFeature ?? feature));
    };

    const onSelectFeature = key => {
        setFeature(key);
        handleSearch(key);
        toggleFeatureModal();
    };

    const renderedLines = useMemo(() =>
        currentConfiguration.map(({ id, nodes = [], position_x: x1 = 0, position_y: y1 = 0 }) => {
            const startingX = x1 + 100;
            const startingY = y1 + (nodeHeights[id] ?? 0);

            return (
                <Fragment key={ `node-${ id }` }>
                    <circle cx={ startingX ?? 0 } cy={ startingY ?? 0 } fill={ primaryColor } r="5" />
                    {nodes.map(({ id: toId }) => {
                        const finishingNode = currentConfiguration.find(({ id }) => id === toId);
                        const finishingX = finishingNode?.position_x + 100;
                        const finishingY = finishingNode?.position_y;

                        return (
                            <line
                                key={ `line-${ id }-${ toId }` }
                                markerEnd="url(#arrowhead)"
                                stroke={ primaryLighterColor }
                                strokeWidth="2"
                                x1={ startingX }
                                x2={ finishingX }
                                y1={ startingY }
                                y2={ finishingY }
                            />
                        );
                    })}
                </Fragment>
            );
        })
    , [currentConfiguration, primaryColor, primaryLighterColor, renderLines]);

    return (
        <Container padding="10px">
            <FeatureModal
                allowDelete
                active={ featureModal }
                onSelect={ onSelectFeature }
                onToggle={ toggleFeatureModal }
            />
            <Container alignItems="center" flexDirection="row" gap="10px">
                <FormGroup label={ t('Feature') }>
                    <Container flexDirection="row" gap="10px">
                        <Input { ...featureInput } width="200px" onEnterPress={ () => handleSearch() } />
                        <ExpandButton circle icon="search" loading={ featureConfigurationLoading } size={ 40 } onClick={ () => handleSearch() } />
                        <ExpandButton circle icon="open_in_new" size={ 40 } onClick={ toggleFeatureModal } />
                    </Container>
                </FormGroup>
                <FormGroup label={ t('Nodos') } >
                    <Input { ...searchNodeInput } width="100px" />
                </FormGroup>
                <FormGroup label={ t('Padres de') } >
                    <Input { ...searchParentInput } width="100px" />
                </FormGroup>
                <FormGroup label={ t('Hijos de') } >
                    <Input { ...searchChildrenInput } width="100px" />
                </FormGroup>
                <Checkbox { ...finishingPointCheck } label={ t('Marcar puntas') } width="150px" />
                <FormGroup label={ t('Default X') } >
                    <Input { ...defaultXInput } type="number" width="100px" />
                </FormGroup>
                <FormGroup label={ t('Default Y') } >
                    <Input { ...defaultYInput } type="number" width="100px" />
                </FormGroup>
                <Container alignItems="flex-end" flex="1">
                    <ExpandButton circle icon="add" size={ 40 } onClick={ () => setEditing({
                        feature,
                        position_x: Number(defaultX ?? 0),
                        position_y: Number(defaultY ?? 0) })
                    }
                    />
                </Container>
            </Container>
            <ZoomableContainer
                backgroundColor="#f5f5f5"
                height="600px"
                margin="0px"
                width="100%"
                onDoubleClick={ handleDoubleClick }
                onZoomChange={ setCurrentZoom }
            >
                {
                    renderLines &&
                    <SVGContainer height="100%" width="100%">
                        <defs>
                            <marker id="arrowhead" markerHeight="7" markerWidth="10" orient="auto" refX="10" refY="3.5">
                                <polygon fill={ primaryColor } points="0 0, 10 3.5, 0 5" />
                            </marker>
                        </defs>
                        { renderedLines }
                    </SVGContainer>
                }
                { currentConfiguration.map(data => (
                    <ChatbotNode
                        currentDragging={ dragging }
                        currentZoom={ currentZoom }
                        data={ data }
                        highlight={ highlightedNodes.includes(data?.id) }
                        key={ data?.id }
                        setCurrentDragging={ setDragging }
                        setHeight={ height => setNodeHeightsValue({ [data?.id]: height }) }
                        setPosition={ (x, y) => handlePositionChange(data?.id, x, y) }
                        onEdit={ () => setEditing(data) }
                        onMouseUp={ () => handleMouseUp(data?.id) }
                    />
                )) }
            </ZoomableContainer>
            <ButtonFooter margin="20px 0px 0px 0px">
                <ExpandButton
                    caption={ t('Guardar esquema') }
                    disabled={ featureConfigurationLoading }
                    size={ 100 }
                    width={ 200 }
                    onClick={ handleSaveSchema }
                />
                <Checkbox
                    { ...autosaveInput }
                    label={ t('Autoguardar') }
                />
            </ButtonFooter>
            <NodeModal
                active={ !isEmpty(editing) }
                data={ editing }
                onToggle={ () => setEditing(null) }
            />
        </Container>
    );
};

export default ChatbotFeatureConfiguration;
